import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Field, Fields, reduxForm, SubmissionError } from 'redux-form';

import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import IconLink from 'components/icons/IconLink';

import {
  createSalesRepresentative,
  updateSalesRepresentative,
} from 'redux/modules/salesRepresentatives';
import {
  addReferralSourceResource,
  removeReferralSourceResource,
} from 'redux/modules/referralSources';
import {
  requestFetchReferralSources,
  requestSearchReferralSources,
} from 'redux/modules/referralSources';
import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  iconLink: {
    margin: '0 8px -7px 0',
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);

    delete normalizedValues['Referral Source'];

    return normalizedValues;
  };
  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    const addedReferralSource = values['Referral Source'].filter(item =>
      props.initialValues['Referral Source'].filter(initialItem =>
        initialItem.value === item.value).length === 0);
    const removedReferralSource = props.initialValues['Referral Source'].filter(initialItem =>
      values['Referral Source'].filter(item =>
        item.value === initialItem.value).length === 0);
    delete diffValues['Referral Source'];
    return new Promise((resolve, reject) => {
      if (Object.keys(diffValues).length > 0) {
        dispatch(updateSalesRepresentative({
          values: {
            ID: props.ID,
            ...diffValues,
            ...diffValues['Middle Name'] === ''
              && { 'Middle Name': null },
            ...diffValues.Email === ''
              && { Email: null },
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
    .then(() => new Promise((resolve, reject) => {
        if (addedReferralSource.length > 0) {
          Promise.all(
            addedReferralSource.map(item =>
              new Promise((allItemResolve, allItemReject) => {
                dispatch(addReferralSourceResource({
                  values: {
                    ID: item.value,
                    'Sales Representative ID': [ props.ID ],
                  },
                  resolve: allItemResolve,
                  reject: allItemReject,
                }))
              })
            )).then(() => {
            resolve();
          });      
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedReferralSource.length > 0) {
          Promise.all(
            removedReferralSource.map(item =>
              new Promise((allItemResolve, allItemReject) => {
                dispatch(removeReferralSourceResource({
                  values: {
                    ID: item.value,
                    'Sales Representative ID': [ props.ID ],
                  },
                  resolve: allItemResolve,
                  reject: allItemReject,
                }))
              })
            )).then(() => {
            resolve();
          });      
        } else {
          resolve();
        }
      })).then(res => res,
      (err) => {
        throw new SubmissionError(err);
      });
  }
  return new Promise((resolve, reject) => {
    dispatch(createSalesRepresentative({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  })
  .then((createValues) => new Promise((resolve, reject) => {
    if (values['Referral Source'] && values['Referral Source'].length > 0) {
      Promise.all(
        values['Referral Source'].map(item =>
          new Promise((allItemResolve, allItemReject) => {
            dispatch(addReferralSourceResource({
              values: {
                ID: item.value,
                'Sales Representative ID': [ createValues.ID ],
              },
              resolve: allItemResolve,
              reject: allItemReject,
            }))
          })
        )).then((values) => {
        resolve(createValues);
      });      
    } else {
      resolve(createValues);
    }
  })).then(res => res,
    (err) => {
      throw new SubmissionError(err);
    });
}

const SalesRepresentativeCreateEditForm = ({ className, classes, handleSubmit, array, actions }) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
        sm={4}
      >
        <Field
          id="First Name"
          name="First Name"
          label="First Name"
          component={TextField}
          validate={[validate.required]}
          errorPlacement="right"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={4}
      >
        <Field
          id="Middle Name"
          name="Middle Name"
          label="Middle Name"
          component={TextField}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={4}
      >
        <Field
          id="Last Name"
          name="Last Name"
          label="Last Name"
          component={TextField}
          validate={[validate.required]}
          errorPlacement="left"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
        sm={5}
      >
        <Field
          id="Phone"
          name="Phone"
          label="Phone"
          component={TextField}
          validate={[validate.required, validate.phone]}
          format={value => (value ? value.replace('+1 ', '') : '')}
          normalize={normalize.phone}
          maxLength="14"
          errorPlacement="right"
          startAdornment={
            <InputAdornment position="start">
              <TextFieldAdornmentText>
                +1
              </TextFieldAdornmentText>
            </InputAdornment>
          }
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={7}
      >
        <Field
          id="Email"
          name="Email"
          label="Email"
          component={TextField}
          validate={[validate.email]}
          errorPlacement="left"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={12}>
        <Fields
          label={(
            <Fragment>
              <IconLink className={classes.iconLink} />
              Linked Referral Source
            </Fragment>
          )}
          names={[
            'Referral Source Name',
            'Referral Source',
          ]}
          autosuggestFieldName="Referral Source Name"
          fieldArrayName="Referral Source"
          placeholder="-Select Referral-"
          component={MultiselectAutosuggestField}
          renderSuggestionContent={suggestion =>
            suggestion['Name of source']}
          renderInputContent={suggestion =>
            suggestion['Name of source']}
          onSuggestionSelected={(suggestion) => {
            array.push('Referral Source', {
              value: suggestion.ID,
              label: suggestion['Name of source'],
            });
            return '';
          }}
          fetchSuggestions={({ value, limit, offset, fieldArrayValue }) => (
            value.length > 0 || (fieldArrayValue && fieldArrayValue.length > 0)
              ? requestSearchReferralSources({
                filters: [
                  ...value.length > 0
                    ? [{
                      name: 'Name of source',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...fieldArrayValue && fieldArrayValue.length > 0
                    ? fieldArrayValue.map(arrayItem => ({
                      name: 'ID',
                      operation: 'ne',
                      val: arrayItem.value,
                    }))
                    : [],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchReferralSources({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          keepOpenAfterSelection
          suggestionsPlacement="top"
        />
      </Grid>
    </Grid>
  </form>
);

SalesRepresentativeCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

SalesRepresentativeCreateEditForm.defaultProps = {
  className: '',
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  onSubmit,
})(withStyles(styles)(SalesRepresentativeCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/SalesRepresentativeCreateEditDialog/SalesRepresentativeCreateEditForm.js